// GPS trace recorder (U19, KTD19).
//
// Records what the phone's Geolocation API delivers during one drive, so a
// later unit (U20) can replay it and measure delivery lag, accuracy, and how
// heading behaves when the car stops (R14, R15). The logger page drives it,
// and the U21 prototype page reuses it so every prototype drive is a trace.
//
// The recorder is pure: callers hand it each fix, error, visibility change,
// wake-lock outcome, and tap with a timestamp, and it builds the trace. No
// browser API is touched here except inside create_idb_store, so node tests
// exercise everything else directly.
//
// Every t is epoch milliseconds. A fix keeps the browser's own
// position.timestamp as t_fix and the time its callback ran as t_arrival,
// separately, since t_arrival minus t_fix is the delivery lag. A value the
// browser leaves out (heading and speed while stationary, altitude on many
// fixes) is recorded as null, never zero, so "no heading" stays distinct from
// "heading north".
//
// The planned list holds house numbers the operator expects to pass. Each tap
// marks the next planned number as passed at the tap time; skip advances past
// a missed house without a time; undo removes the last mark or skip. Marks are
// one per planned entry, in order, so the next planned number is always
// planned[marks.length].
//
// The U21 prototype adds two event kinds through event(): 'readout', whose
// detail is the readout shown at a fix (state.jsy readout_record), logged at
// that fix's t_arrival, and 'region', whose detail is the data version and
// tile count whenever newly loaded tiles change the network. Both fit schema
// 1 as it stands, so a prototype drive replays exactly.
//
// A drive the phone discarded mid-recording (iOS reloads a backgrounded
// page) continues with resume(): the saved trace becomes this recorder's
// trace, still recording, with a 'resume' event marking the gap, so it stays
// one drive. A stopped drive is never resumed.
//
// Real traces are private position history: they stay in the phone's
// IndexedDB and leave only through an export the operator triggers. Nothing
// in this module talks to a server.

export const SCHEMA = 1
export const KIND = 'simplemap-trace'
export const CLOCK = 'epoch-ms'

export const TRACE_KEYS = @#
  'schema', 'kind', 'clock', 'drive_id', 'started_at', 'stopped_at'
  'user_agent', 'watch_lost', 'planned', 'fixes', 'marks', 'events'
export const FIX_KEYS = @#
  't_fix', 't_arrival', 'lat', 'lon', 'accuracy', 'heading', 'speed'
  'altitude', 'altitude_accuracy'
export const MARK_KEYS = @# 't', 'number', 'kind'
export const EVENT_KEYS = @# 't', 'kind', 'detail'

// GeolocationPositionError codes
export const GEO_ERRORS = @{} 1: 'permission_denied', 2: 'position_unavailable', 3: 'timeout'


function num_or_null(v) ::
  if null == v :: return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null


// One fix record from a GeolocationPosition (or any object of that shape).
export function fix_record(position, t_arrival) ::
  const c = position.coords || {}
  return @{}
    t_fix: num_or_null(position.timestamp)
    t_arrival: num_or_null(t_arrival)
    lat: num_or_null(c.latitude)
    lon: num_or_null(c.longitude)
    accuracy: num_or_null(c.accuracy)
    heading: num_or_null(c.heading)
    speed: num_or_null(c.speed)
    altitude: num_or_null(c.altitude)
    altitude_accuracy: num_or_null(c.altitudeAccuracy)


// A sortable, filename-safe drive id from the start time: 20261010T143000Z
export function drive_id_for(t) ::
  return new Date(t).toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d+Z$/, 'Z')


// House numbers from the operator's typed list. Entries are separated by new
// lines, commas, or semicolons; each contributes its leading number, so
// "1234 Main St" reads as 1234. An entry of bare numbers separated by spaces
// contributes all of them.
export function parse_planned(text) ::
  const out = []
  for const piece of String(text || '').split(/[\n,;]+/) ::
    const s = piece.trim()
    if /^[\d\s]+$/.test(s) ::
      for const tok of s.split(/\s+/) ::
        if tok :: out.push @ Number(tok)
      continue
    const m = /^(\d+)/.exec(s)
    if m :: out.push @ Number(m[1])
  return out


export function export_filename(trace) ::
  return `simplemap-trace-${trace.drive_id || 'unknown'}.json`


// Problems with an exported trace against schema 1, as a list of strings.
// An empty list means the trace matches.
export function check_trace(trace) ::
  const problems = []
  if ! trace || 'object' !== typeof trace ::
    return @# 'trace is not an object'
  check_keys @ problems, 'trace', trace, TRACE_KEYS
  if SCHEMA !== trace.schema :: problems.push @ `schema ${trace.schema}`
  if KIND !== trace.kind :: problems.push @ `kind ${trace.kind}`
  for const k of ['planned', 'fixes', 'marks', 'events'] ::
    if ! Array.isArray(trace[k]) :: problems.push @ `${k} is not an array`
  if problems.length :: return problems

  trace.fixes.forEach @\ f, i :: check_keys(problems, `fixes[${i}]`, f, FIX_KEYS)
  trace.marks.forEach @\ m, i :: check_keys(problems, `marks[${i}]`, m, MARK_KEYS)
  trace.events.forEach @\ e, i :: check_keys(problems, `events[${i}]`, e, EVENT_KEYS)
  for let i = 1; i < trace.fixes.length; i++ ::
    if trace.fixes[i].t_arrival < trace.fixes[i - 1].t_arrival ::
      problems.push @ `fixes[${i}] out of arrival order`
  for let i = 1; i < trace.events.length; i++ ::
    if trace.events[i].t < trace.events[i - 1].t ::
      problems.push @ `events[${i}] out of time order`
  return problems


function check_keys(problems, path, obj, keys) ::
  const have = Object.keys(obj || {}).sort().join(',')
  const want = [...keys].sort().join(',')
  if have !== want :: problems.push @ `${path} keys ${have}`


// The recorder for one drive.
//
// opts.drive_id overrides the id derived from the start time.
// opts.user_agent is stored in the trace as given.
// opts.store, when given, receives the exported trace on flush().
export function create_recorder(opts = {}) ::
  const trace = @{}
    schema: SCHEMA
    kind: KIND
    clock: CLOCK
    drive_id: opts.drive_id || null
    started_at: null
    stopped_at: null
    user_agent: opts.user_agent ?? null
    watch_lost: false
    planned: []
    fixes: []
    marks: []
    events: []

  let status = 'idle'
  let last_fix = null
  let dirty = false

  return @{}
    trace
    start, resume, stop, plan, fix, error, visibility, wakelock, event
    mark, skip, undo, progress, fix_age, to_json, flush
    get_status: () => status
    get_last_fix: () => last_fix
    is_dirty: () => dirty

  function recording() ::
    return 'recording' === status

  function start(t) ::
    if 'idle' !== status :: return false
    status = 'recording'
    trace.started_at = t
    if ! trace.drive_id :: trace.drive_id = drive_id_for(t)
    event @ 'start', t
    return true

  function resume(saved, t) ::
    if 'idle' !== status || ! saved || null !== saved.stopped_at || ! saved.drive_id :: return false
    for const k of TRACE_KEYS ::
      if k in saved :: trace[k] = saved[k]
    status = 'recording'
    last_fix = trace.fixes.length ? trace.fixes[trace.fixes.length - 1] : null
    event @ 'resume', t
    return true

  function stop(t) ::
    if ! recording() :: return false
    status = 'stopped'
    trace.stopped_at = t
    event @ 'stop', t
    return true

  // Replace the planned list. Clears any marks, since they pointed into the
  // old list.
  function plan(numbers, t = null) ::
    if 'stopped' === status :: return false
    trace.planned = numbers.map @ n => Number(n)
    trace.marks = []
    dirty = true
    if recording() :: event @ 'plan', t, trace.planned.length
    return true

  function fix(position, t_arrival) ::
    if ! recording() :: return null
    const rec = fix_record(position, t_arrival)
    trace.fixes.push @ rec
    last_fix = rec
    dirty = true
    return rec

  // A GeolocationPositionError. Timeouts and unavailable positions are
  // transient: the watch keeps running and the next fix records normally.
  // Permission denied ends the watch, so the drive is marked as having lost
  // it. Returns true when the watch is lost.
  function error(err, t) ::
    if ! recording() :: return false
    const code = err && null != err.code ? err.code : null
    const lost = 1 === code
    const detail = @{}
      code: code
      name: GEO_ERRORS[code] || 'unknown'
      message: err && err.message ? String(err.message) : null
    event @ 'geo_error', t, detail
    if lost :: trace.watch_lost = true
    return lost

  function visibility(state, t) ::
    return event @ 'visibility', t, state

  // outcome is a short word: acquired, released, unsupported, failed
  function wakelock(outcome, t, message = null) ::
    const detail = null == message ? outcome : `${outcome}: ${message}`
    return event @ 'wakelock', t, detail

  function event(kind, t, detail = null) ::
    if 'idle' === status :: return false
    trace.events.push @: t, kind, detail
    dirty = true
    return true

  function progress() ::
    const index = trace.marks.length
    const total = trace.planned.length
    return @{}
      index: index
      total: total
      next: index < total ? trace.planned[index] : null
      finished: total > 0 && index >= total

  function advance(t, kind) ::
    if ! recording() :: return null
    const p = progress()
    if null === p.next :: return null
    const rec = @{} t: t, number: p.next, kind: kind
    trace.marks.push @ rec
    dirty = true
    return rec

  // The passenger tapped as the next planned house went by.
  function mark(t) ::
    return advance @ t, 'pass'

  // The next planned house was missed; advance without a time.
  function skip() ::
    return advance @ null, 'skip'

  // Remove the last mark or skip. The removal is logged as an event, so a
  // corrected mis-tap stays visible in the trace.
  function undo(t) ::
    if ! recording() || ! trace.marks.length :: return null
    const rec = trace.marks.pop()
    event @ 'undo', t, rec.number
    dirty = true
    return rec

  // Milliseconds since the last fix arrived, or null before the first fix.
  function fix_age(now) ::
    return last_fix ? now - last_fix.t_arrival : null

  // The trace as exported: fixes in arrival order, events in time order.
  function to_json() ::
    const fixes = [...trace.fixes].sort @ (a, b) => a.t_arrival - b.t_arrival
    const events = [...trace.events].sort @ (a, b) => a.t - b.t
    return @{}
      ... trace
      planned: [...trace.planned]
      fixes: fixes.map @ f => ({...f})
      marks: trace.marks.map @ m => ({...m})
      events: events.map @ e => ({...e})

  // A store that fails leaves the trace dirty, so the next flush tries again.
  async function flush() ::
    if ! opts.store || ! trace.drive_id :: return false
    dirty = false
    try ::
      await opts.store.put @ to_json()
    catch err ::
      dirty = true
      throw err
    return true


// Drive storage: put(trace), get(drive_id), list(), remove(drive_id).
// list() returns summaries, newest first.

function summary(trace) ::
  return @{}
    drive_id: trace.drive_id
    started_at: trace.started_at
    stopped_at: trace.stopped_at
    fixes: trace.fixes.length
    marks: trace.marks.length
    watch_lost: trace.watch_lost

function newest_first(a, b) ::
  return (b.started_at || 0) - (a.started_at || 0)


export function create_memory_store() ::
  const drives = new Map()
  return @{} put, get, list, remove

  async function put(trace) ::
    drives.set @ trace.drive_id, JSON.parse(JSON.stringify(trace))

  async function get(drive_id) ::
    const trace = drives.get(drive_id)
    return trace ? JSON.parse(JSON.stringify(trace)) : null

  async function list() ::
    return [...drives.values()].map(summary).sort(newest_first)

  async function remove(drive_id) ::
    drives.delete @ drive_id


// IndexedDB adapter for the browser, one record per drive keyed by drive_id.
// Not exercised by the node tests.
export function create_idb_store(db_name = 'simplemap-traces') ::
  const STORE = 'drives'
  let db_p = null
  return @{} put, get, list, remove

  function open() ::
    if ! db_p :: db_p = new Promise(open_db)
    return db_p

  function open_db(resolve, reject) ::
    const req = indexedDB.open(db_name, 1)
    const store_opts = @{} keyPath: 'drive_id'
    req.onupgradeneeded = function () :: req.result.createObjectStore(STORE, store_opts)
    req.onsuccess = function () :: resolve(req.result)
    req.onerror = function () :: reject(req.error)

  async function tx(mode, fn) ::
    const db = await open()
    const t = db.transaction(STORE, mode)
    const req = fn(t.objectStore(STORE))
    return new Promise @\ resolve, reject ::
      t.oncomplete = function () :: resolve(req ? req.result : undefined)
      t.onerror = function () :: reject(t.error)
      t.onabort = function () :: reject(t.error)

  async function put(trace) ::
    await tx @ 'readwrite', s => s.put(trace)

  async function get(drive_id) ::
    return (await tx('readonly', s => s.get(drive_id))) || null

  async function list() ::
    const all = await tx @ 'readonly', s => s.getAll()
    return all.map(summary).sort(newest_first)

  async function remove(drive_id) ::
    await tx @ 'readwrite', s => s.delete(drive_id)
